/**
 * Helpers DOM — création d'éléments typée, sans innerHTML interpolé.
 *
 * Toute construction de DOM du frontend passe par ici. Les seules écritures
 * de style inline autorisées vivent dans `setStyle`/`setStyles` (valeurs
 * calculées : aspect-ratio d'image, largeur de barre de progression…).
 */

type Child = Node | string | null | undefined | false;

type Attrs = Record<string, unknown>;

/** Propriétés DOM posées directement (l'attribut HTML ne suffit pas). */
const DOM_PROPS = new Set(["value", "checked", "disabled", "selected", "maxLength", "minLength"]);

/**
 * Hyperscript minimal : h("div", { class: "x", onclick: fn }, ...children).
 * - onclick / oninput… (fonctions) → propriété événement ;
 * - class → className ;
 * - value / checked / disabled… → propriété DOM ;
 * - tout le reste → setAttribute.
 */
export function h<K extends keyof HTMLElementTagNameMap>(
	tag: K,
	attrs: Attrs | null = null,
	...children: Child[]
): HTMLElementTagNameMap[K] {
	const el = document.createElement(tag);
	if (attrs) {
		for (const [key, value] of Object.entries(attrs)) {
			if (value === null || value === undefined || value === false) continue;
			if (key.startsWith("on")) {
				(el as unknown as Record<string, unknown>)[key] = value;
			} else if (key === "class") {
				el.className = String(value);
			} else if (DOM_PROPS.has(key)) {
				(el as unknown as Record<string, unknown>)[key] = value;
			} else {
				el.setAttribute(key, value === true ? "" : String(value));
			}
		}
	}
	append(el, children);
	return el;
}

function append(el: HTMLElement, children: Child[]): void {
	for (const child of children) {
		if (child === null || child === undefined || child === false) continue;
		el.append(child instanceof Node ? child : document.createTextNode(String(child)));
	}
}

/** Remplace tout le contenu d'un élément par les enfants donnés. */
export function setChildren(el: HTMLElement, ...children: Child[]): void {
	el.replaceChildren();
	append(el, children);
}

/** Retire un élément de son parent s'il y est. */
export function remove(el: Element): void {
	el.parentElement?.removeChild(el);
}

/**
 * Pose une propriété de style calculée (cas légitimes uniquement :
 * dimensions, progression). À utiliser avec parcimonie — le CSS vit dans
 * src/css/, pas ici.
 */
export function setStyle(el: HTMLElement, property: string, value: string): void {
	el.style.setProperty(property, value);
}

/** Format SVG inline pour icônes (chemin + viewBox). */
export function svgIcon(pathData: string, viewBox = "0 0 24 24"): SVGSVGElement {
	const NS = "http://www.w3.org/2000/svg";
	const svg = document.createElementNS(NS, "svg");
	svg.setAttribute("viewBox", viewBox);
	svg.setAttribute("fill", "none");
	svg.setAttribute("stroke", "currentColor");
	svg.setAttribute("stroke-width", "2");
	svg.setAttribute("stroke-linecap", "round");
	svg.setAttribute("stroke-linejoin", "round");
	svg.classList.add("rail-icon");
	const path = document.createElementNS(NS, "path");
	path.setAttribute("d", pathData);
	svg.appendChild(path);
	return svg;
}
